import { FC, useMemo } from "react";
import { Stack, Table, TableContainer, Paper } from "@mui/material";
import { InternalBillingExplorerTotalNoDataView } from "./components/InternalBillingExplorerTotalTableHeaderNoDataView";
import { InternalBillingExplorerTotalTableHeader } from "./components/InternalBillingExplorerTotalTableHeaderTableHeader";
import { InternalBillingExplorerTotalTableBodyContent } from "./components/InternalBillingExplorerTotalTableHeaderTableBodyContent";
import { InternalBillingExplorerTotalCellRenderer } from "./components/InternalBillingExplorerTotalTableHeaderCellRenderer";
import { InternalBillingExplorerTotalHeaderRenderer } from "./components/InternalBillingExplorerTotalTableHeaderHeaderRenderer";
import { wrapperStyles } from "./utils/constants/tableStyles";
import { getBillingExplorerMutatedData } from "../../utils/helpers/helpers";
import {
  ResourceExplorerGroupingNullable,
  ResourceExplorerWidget,
} from "@/services/cloudchipr.api";
import { readDataByDotSeparatedString } from "@/components/pages/common/ag-grid/utils/helpers/readDataByDotSeparatedString";
import { InternalBillingExplorerCardGridToolbar } from "@/components/unauthorized-pages/internal-billing-explorer/components/table/InternalBillingExplorerCardGridToolbar";
import { ResourceExplorerTableFooter } from "@/components/pages/resource-explorer/components/resource-explorer-card/components/billing-explorer-data-grid/components/footer/ResourceExplorerTableFooter";
import { useAppSelector } from "@/store/hooks";
import { resourceExplorerInternalPossibleGroupingsFormattedDataSelector } from "@/store/resource-explorer/selectors/internal/resourceExplorerInternalPossibleGroupingsFormattedDataSelector";
import { getBillingExplorerDataGridColumnDefs } from "@/components/pages/resource-explorer/components/resource-explorer-card/components/billing-explorer-data-grid/components/column-defs/billingExplorerTotalViewColumnDefs";

interface InternalResourceExplorerTotalViewDataGridProps {
  groupBy: ResourceExplorerGroupingNullable;
  data: ResourceExplorerWidget;
  organisationId: string;
}

export const InternalBillingExplorerTotalViewGrid: FC<
  InternalResourceExplorerTotalViewDataGridProps
> = ({ groupBy, data, organisationId }) => {
  const { gridData, aggregatedData } = useMemo(
    () => getBillingExplorerMutatedData(data, groupBy),
    [data, groupBy],
  );

  const groupingLabels = useAppSelector(
    (state) =>
      resourceExplorerInternalPossibleGroupingsFormattedDataSelector(
        state,
        organisationId,
      )?.groupingLabelMap,
  );

  const columnDefs = useMemo(() => {
    return getBillingExplorerDataGridColumnDefs(groupBy).filter(
      (col) => col.field !== "quarterly_forecast",
    );
  }, [groupBy]);

  const fieldNames = useMemo(() => {
    return columnDefs.map((col) => col.field || "");
  }, [columnDefs]);

  if (!gridData || gridData.length === 0) {
    return <InternalBillingExplorerTotalNoDataView />;
  }

  return (
    <Stack position="relative" height="100%">
      <InternalBillingExplorerCardGridToolbar
        groupBy={groupBy}
        organisationId={organisationId}
      />

      <TableContainer component={Paper} sx={wrapperStyles}>
        <Table>
          <InternalBillingExplorerTotalTableHeader
            columnDefs={columnDefs}
            renderHeaderComponent={renderHeaderComponent}
          />
          <InternalBillingExplorerTotalTableBodyContent
            gridData={gridData}
            fieldNames={fieldNames}
            getNestedPropertyValue={readDataByDotSeparatedString}
            renderCellComponent={renderCellComponent}
          />
        </Table>
      </TableContainer>

      {aggregatedData && (
        <ResourceExplorerTableFooter
          count={+aggregatedData.metadata.count}
          threshold={+aggregatedData.metadata.threshold}
          totalCost={aggregatedData?.total_cost}
          gridDataCount={gridData.length}
        />
      )}
    </Stack>
  );

  function renderHeaderComponent(col: any, index: number) {
    return (
      <InternalBillingExplorerTotalHeaderRenderer
        col={col}
        index={index}
        groupBy={groupBy}
        groupingLabels={groupingLabels}
        totalCost={data.total_cost}
        previousPeriodTotalCost={data.previous_period_total_cost}
        forecasted_costs={data.forecasted_costs}
        dataCount={gridData.length}
      />
    );
  }

  function renderCellComponent(field: string, value: any, row: any) {
    return (
      <InternalBillingExplorerTotalCellRenderer
        field={field}
        value={value}
        row={row}
      />
    );
  }
};
